import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import type { OperationVariables } from '@apollo/client'
import type { RowSelectionState } from '@tanstack/react-table'
import type { AlgoliaHit } from 'instantsearch.js'

import { useActiveUser } from 'Hooks'

import type { SearchResultsDocument } from 'Types/globalSearch/searchResultsDocument.types'

import { resolveSubmitColor } from './resolveSubmitColor'
import type { TagAutocompleteHandle } from './TagAutocomplete.types'
import type { Tag } from './tagging.types'
import type { TaggingEntityAdapter } from './taggingEntityAdapter.types'
import type { TaggingTableContextType } from './TaggingProvider.types'
import { INITIAL_STATE, taggingReducer } from './taggingReducer'
import { useOrgTags } from './useOrgTags'
import { useTagDispatchWrappers } from './useTagDispatchWrappers'
import { useTagKeyboardHandlers } from './useTagKeyboardHandlers'
import { useTagMutationActions } from './useTagMutationActions'
import { useTagOutsideClickDismiss } from './useTagOutsideClickDismiss'

const TAGGING_SYNC_TIMEOUT_MS = 15_000

type SyncInterruption = 'timeout' | 'unmount'

interface UseTaggingTableOptions<
  TAddData,
  TAddVariables extends OperationVariables,
  TRemoveData,
  TRemoveVariables extends OperationVariables,
> {
  adapter: TaggingEntityAdapter<
    TAddData,
    TAddVariables,
    TRemoveData,
    TRemoveVariables
  >
  hits: AlgoliaHit<SearchResultsDocument>[]
  refresh: () => void
}

export const useTaggingTable = <
  TAddData,
  TAddVariables extends OperationVariables,
  TRemoveData,
  TRemoveVariables extends OperationVariables,
>(
  options: UseTaggingTableOptions<
    TAddData,
    TAddVariables,
    TRemoveData,
    TRemoveVariables
  >,
): TaggingTableContextType => {
  const { adapter, hits, refresh } = options
  const { activeOrganisation } = useActiveUser()
  const orgId = String(activeOrganisation?.id ?? '')

  const [state, dispatch] = useReducer(taggingReducer, INITIAL_STATE)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [syncAction, setSyncAction] = useState<'add' | 'remove' | null>(null)

  const { orgTags, refetchTags, resolveOrgTagId } = useOrgTags(orgId)

  const autocompleteRef = useRef<TagAutocompleteHandle | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const handleSubmitRef = useRef<() => void>(() => {})
  const interruptSyncRef = useRef<
    ((reason: SyncInterruption) => void) | null
  >(null)
  const isMountedRef = useRef(true)
  const justEnteredEditRef = useRef(false)
  const pendingRefreshRef = useRef(false)
  const prevModeRef = useRef(state.mode)
  const prevSelectionRef = useRef<RowSelectionState>(rowSelection)
  const resolveSyncRef = useRef<(() => void) | null>(null)
  const syncActionRef = useRef<'add' | 'remove' | null>(null)
  const hitsRef = useRef(hits)

  hitsRef.current = hits

  const {
    enterEditMode: dispatchEnterEditMode,
    exitEditMode,
    setActiveEditorRowId,
    setInputText,
    setSelectedColor,
  } = useTagDispatchWrappers(dispatch)

  const getEntityIdsWithTag = useCallback(
    (entityIds: string[], tag: Tag) => {
      return entityIds.filter(entityId => {
        const hit = hitsRef.current.find(h => String(h.id) === entityId)
        const rowTags = hit ? adapter.getTagsFromHit(hit) : []

        return rowTags.some(rowTag => String(rowTag.id) === tag.id)
      })
    },
    [adapter],
  )

  const getEntityIdsWithTagName = useCallback(
    (entityIds: string[], name: string) => {
      return entityIds.filter(entityId => {
        const hit = hitsRef.current.find(h => String(h.id) === entityId)
        const rowTags = hit ? adapter.getTagsFromHit(hit) : []

        return rowTags.some(
          rowTag => rowTag.name.trim().toLowerCase() === name,
        )
      })
    },
    [adapter],
  )

  const completeTagMutation = useCallback(async (action: 'add' | 'remove') => {
    const hitsSyncPromise = new Promise<void>(resolve => {
      resolveSyncRef.current = resolve
    })
    const interruptionPromise = new Promise<{
      kind: SyncInterruption
    }>(resolve => {
      interruptSyncRef.current = kind => resolve({ kind })
    })
    const timeoutId = window.setTimeout(() => {
      interruptSyncRef.current?.('timeout')
    }, TAGGING_SYNC_TIMEOUT_MS)

    pendingRefreshRef.current = true

    let refreshError: unknown

    try {
      refresh()
    } catch (error) {
      refreshError = error
      resolveSyncRef.current?.()
    }

    const settledPromise = Promise.allSettled([
      Promise.resolve().then(refetchTags),
      hitsSyncPromise,
    ]).then(results => ({ kind: 'settled' as const, results }))
    const syncResult = await Promise.race([
      settledPromise,
      interruptionPromise,
    ])
    const shouldExitAddMode =
      action === 'add' && pendingRefreshRef.current

    window.clearTimeout(timeoutId)
    interruptSyncRef.current = null
    pendingRefreshRef.current = false

    const resolveSync = resolveSyncRef.current

    resolveSyncRef.current = null
    resolveSync?.()
    syncActionRef.current = null

    if (isMountedRef.current) {
      if (shouldExitAddMode) {
        dispatch({ type: 'EXIT_EDIT_MODE' })
      }

      setSyncAction(null)
    }

    if (syncResult.kind !== 'settled') {
      if (syncResult.kind === 'unmount') {
        return
      }

      throw new Error('Timed out waiting for tagging search synchronization')
    }

    if (refreshError) {
      throw refreshError instanceof Error
        ? refreshError
        : new Error('Failed to refresh tagging search results')
    }

    const [tagsResult] = syncResult.results

    if (tagsResult.status === 'rejected') {
      throw tagsResult.reason
    }
  }, [refetchTags, refresh])

  const handleAddCompleted = useCallback(() => {
    if (!isMountedRef.current) {
      return
    }

    syncActionRef.current = 'add'
    setSyncAction('add')

    return completeTagMutation('add')
  }, [completeTagMutation])

  const handleRemoveCompleted = useCallback(() => {
    if (!isMountedRef.current) {
      return
    }

    syncActionRef.current = 'remove'
    setSyncAction('remove')

    return completeTagMutation('remove')
  }, [completeTagMutation])

  const {
    addTag,
    isAddingTag: isAddingTagMutation,
    isRemovingTag: isRemovingTagMutation,
    removeTag,
  } = useTagMutationActions({
    adapter,
    getEntityIdsWithTag,
    getEntityIdsWithTagName,
    onAddCompleted: handleAddCompleted,
    onRemoveCompleted: handleRemoveCompleted,
    orgId,
    resolveOrgTagId,
  })

  const isAddingTag = isAddingTagMutation || syncAction === 'add'
  const isRemovingTag = isRemovingTagMutation || syncAction === 'remove'
  const isEditorOpen = state.mode === 'edit' && state.activeEditorRowId !== null
  const isMutating = isAddingTag || isRemovingTag

  const enterEditMode = useCallback(
    (rowId: string) => {
      if (isMutating) {
        return
      }

      dispatchEnterEditMode(rowId)
    },
    [dispatchEnterEditMode, isMutating],
  )

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
      interruptSyncRef.current?.('unmount')

      const resolveSync = resolveSyncRef.current

      resolveSyncRef.current = null
      pendingRefreshRef.current = false
      syncActionRef.current = null
      resolveSync?.()
    }
  }, [])

  // Guards external callers (TanStack/MobX); internal effects use setRowSelection directly
  const guardedSetRowSelection: typeof setRowSelection = useCallback(
    updater => {
      if (isMutating) {
        return
      }

      setRowSelection(updater)
    },
    [isMutating],
  )

  // --- Derived selection state ---

  const selectedRowIds = useMemo(
    () => Object.keys(rowSelection).filter(id => rowSelection[id]),
    [rowSelection],
  )

  const selectedCount = selectedRowIds.length

  const isRowSelected = useCallback(
    (rowId: string) => !!rowSelection[rowId],
    [rowSelection],
  )

  // --- Selection sync effects ---

  // Effect 1: auto-check triggerRowId when entering edit mode
  useEffect(() => {
    if (state.mode === 'edit' && state.triggerRowId) {
      justEnteredEditRef.current = true
      setRowSelection(prev => ({ ...prev, [state.triggerRowId!]: true }))
    }
  }, [state.mode, state.triggerRowId])

  // Effect 2: clear selection on edit -> read transition
  useEffect(() => {
    if (prevModeRef.current === 'edit' && state.mode === 'read') {
      setRowSelection({})
    }

    prevModeRef.current = state.mode
  }, [state.mode])

  // Effect 3: uncheck-all guard - exit edit mode if no rows selected
  useEffect(() => {
    if (justEnteredEditRef.current) {
      justEnteredEditRef.current = false

      return
    }

    const hasSelectedRows = Object.values(rowSelection).some(Boolean)

    if (state.mode === 'edit' && !hasSelectedRows) {
      exitEditMode()
    }
  }, [rowSelection, state.mode, exitEditMode])

  // Effect 4: reassign editor row when active row is unchecked
  useEffect(() => {
    if (state.mode !== 'edit' || !state.activeEditorRowId) {
      prevSelectionRef.current = rowSelection

      return
    }

    const wasSelected = prevSelectionRef.current[state.activeEditorRowId]
    const isSelected = rowSelection[state.activeEditorRowId]

    if (wasSelected && !isSelected) {
      const currentSelectedIds = Object.keys(rowSelection).filter(
        id => rowSelection[id],
      )

      if (currentSelectedIds.length > 0) {
        const rowOrder = hits.map(h => String(h.id))
        const currentIndex = rowOrder.indexOf(state.activeEditorRowId)
        let nextRowId: string | null = null

        for (let i = currentIndex + 1; i < rowOrder.length; i += 1) {
          if (currentSelectedIds.includes(rowOrder[i])) {
            nextRowId = rowOrder[i]
            break
          }
        }

        if (!nextRowId) {
          for (let i = 0; i < currentIndex; i += 1) {
            if (currentSelectedIds.includes(rowOrder[i])) {
              nextRowId = rowOrder[i]
              break
            }
          }
        }

        if (nextRowId) {
          setActiveEditorRowId(nextRowId)
        }
      }
    }

    prevSelectionRef.current = rowSelection
  }, [
    rowSelection,
    state.mode,
    state.activeEditorRowId,
    setActiveEditorRowId,
    hits,
  ])

  // Effect 5: signal when refresh brings fresh hits
  useEffect(() => {
    if (!syncActionRef.current || !pendingRefreshRef.current) {
      return
    }

    pendingRefreshRef.current = false

    if (syncActionRef.current === 'add') {
      dispatch({ type: 'EXIT_EDIT_MODE' })
    }

    const resolveSync = resolveSyncRef.current

    resolveSyncRef.current = null
    resolveSync?.()
  }, [hits])

  // --- Actions ---

  const canSubmit = state.inputText.trim().length > 0

  const handleSubmit = useCallback(() => {
    if (!canSubmit || isMutating) {
      return
    }

    const trimmed = state.inputText.trim()
    const color = resolveSubmitColor(
      state.inputText,
      state.selectedColor,
      orgTags,
    )

    addTag(selectedRowIds, trimmed, color)
  }, [
    canSubmit,
    isMutating,
    orgTags,
    selectedRowIds,
    state.inputText,
    state.selectedColor,
    addTag,
  ])

  handleSubmitRef.current = handleSubmit

  // --- Keyboard handler ---

  useTagKeyboardHandlers({
    autocompleteRef,
    exitEditMode,
    handleSubmitRef,
    inputRef,
    inputText: state.inputText,
    isMutating,
    mode: state.mode,
  })

  // --- Outside click dismiss (when input is empty) ---

  useTagOutsideClickDismiss({
    exitEditMode,
    inputText: state.inputText,
    isMutating,
    mode: state.mode,
    scopeSelector: '.TaggingTable .Table-row',
  })

  return {
    ...state,
    addTag,
    applyingToCountMessageId: adapter.messages.applyingToCount,
    autocompleteRef,
    canSubmit,
    enterEditMode,
    exitEditMode,
    handleSubmit,
    inputRef,
    isAddingTag,
    isEditorOpen,
    isRemovingTag,
    isRowSelected,
    orgTags,
    removeTag,
    rowSelection,
    selectedCount,
    selectedRowIds,
    setActiveEditorRowId,
    setInputText,
    setRowSelection: guardedSetRowSelection,
    setSelectedColor,
  }
}
